import {
  AttachFile as AttachFileIcon,
  CalendarToday as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  DeviceHub as DeviceIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

const ParticipantDetailsCollapsible = ({
  participantData,
  defaultExpanded = false,
  onExpandChange,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleExpandClick = () => {
    const newState = !expanded;
    setExpanded(newState);
    if (onExpandChange) {
      onExpandChange(newState);
    }
  };

  function transformApiResponse(apiResponse) {
    return {
      participantType: apiResponse?.ai_generated ? 'AI' : 'Human',
      email: apiResponse?.email || null,
      location: apiResponse?.location || null,
      device: apiResponse?.device || null,
      submissionDate: apiResponse?.submitting_date || null,
      // For uploaded transcripts - original filename and content type
      originalFilename: apiResponse?.original_filename || null,
      fileContentType: apiResponse?.file_content_type || null,
    };
  }

  const ParticipantDataTransformed = transformApiResponse(participantData || {});
  const data = ParticipantDataTransformed;

  // Helper to determine if a field should be shown
  const hasValue = (value) => value && value !== '--' && value !== '';

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Tooltip title={expanded ? '' : 'Expand Participant Details'} placement="left">
      <Card
        variant="outlined"
        onClick={!expanded ? handleExpandClick : undefined}
        sx={{
          p: 0,
          cursor: !expanded ? 'pointer' : 'default',
          width: expanded ? '100%' : 48,
          minWidth: expanded ? 280 : 48,
          minHeight: expanded ? 'auto' : 120,
          overflow: 'hidden',
          transition: 'width 0.3s ease, min-width 0.3s ease, min-height 0.3s ease',
          '&:hover': !expanded
            ? {
                backgroundColor: 'rgba(0,0,0,0.04)',
              }
            : {},
        }}
      >
        {/* Collapsed view - vertical strip */}
        <Box
          sx={{
            display: expanded ? 'none' : 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 2,
            px: 1,
            height: '100%',
            minHeight: 120,
          }}
        >
          <PersonIcon color="primary" />
          <IconButton size="small">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Expanded view - full panel */}
        <Box
          sx={{
            display: expanded ? 'block' : 'none',
            opacity: expanded ? 1 : 0,
            transition: 'opacity 0.2s ease 0.1s',
          }}
        >
          <Box
            onClick={handleExpandClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PersonIcon color="primary" />
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                Participant Details
              </Typography>
              <Chip label={data.participantType} color="primary" size="small" sx={{ ml: 1 }} />
            </Box>

            <Tooltip title="Collapse">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleExpandClick();
                }}
                aria-expanded={expanded}
                aria-label="collapse"
                sx={{
                  transform: 'rotate(180deg)',
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Divider />
          <CardContent sx={{ pt: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Original Filename - for uploaded transcripts */}
              {hasValue(data?.originalFilename) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachFileIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                    File:
                  </Typography>
                  <Tooltip title={data?.originalFilename} placement="top">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        wordBreak: 'break-all',
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {data?.originalFilename}
                    </Typography>
                  </Tooltip>
                </Box>
              )}

              {/* Email - only show if present */}
              {hasValue(data?.email) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                    Email:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                    {data?.email}
                  </Typography>
                </Box>
              )}

              {/* Location - only show if present */}
              {hasValue(data?.location) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                    Location:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {data?.location}
                  </Typography>
                </Box>
              )}

              {/* Device - only show if present */}
              {hasValue(data?.device) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DeviceIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                    Device:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {data?.device}
                  </Typography>
                </Box>
              )}

              {/* Submitted Date - only show if present */}
              {hasValue(data?.submissionDate) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                    Submitted:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDate(data.submissionDate)}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Box>
      </Card>
    </Tooltip>
  );
};

export default ParticipantDetailsCollapsible;
